import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase.ts';
import { DashboardStats } from '../types/dashboard.ts';

export const useDashboardStats = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchDashboardStats() {
      try {
        const [properties, payments, bills, tenants] = await Promise.all([
          supabase.from('properties').select('*'),
          supabase.from('payments').select('*'),
          supabase.from('bills').select('*'),
          supabase.from('tenants').select(`
            *,
            property:properties(name)
          `)
        ]);

        // Calculate statistics
        const totalProperties = properties.data?.length || 0;
        const occupiedProperties = properties.data?.filter(p => p.status === 'Rented').length || 0;
        const vacantProperties = totalProperties - occupiedProperties;

        // Calculate this month's collection
        const currentMonth = new Date().getMonth();
        const monthlyCollection = payments.data
          ?.filter(p => new Date(p.payment_date).getMonth() === currentMonth)
          .reduce((sum, payment) => sum + payment.total_amount, 0) || 0;

        setStats({
          totalProperties,
          occupiedProperties,
          vacantProperties,
          monthlyCollection,
          rentCollection: calculateRentCollection(payments.data || [], bills.data || []),
          propertyStatus: calculatePropertyStatus(properties.data || []),
          recentPayments: (payments.data || []).slice(0, 5),
          upcomingLeaseEndings: calculateLeaseEndings(tenants.data || [])
        });
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardStats();
  }, []);

  return { stats, loading, error };
};

// Helper functions
function calculateRentCollection(payments: any[], bills: any[]) {
  const collected: Record<string, number> = {};
  const pending: Record<string, number> = {};

  // Calculate collected rents
  payments.forEach(payment => {
    const month = new Date(payment.payment_date).toLocaleString('default', { month: 'short' });
    collected[month] = (collected[month] || 0) + payment.total_amount;
  });

  // Calculate pending rents
  bills.forEach(bill => {
    if (bill.status === 'Due') {
      const month = bill.month.split(' ')[0].slice(0, 3); // Convert 'January 2024' to 'Jan'
      pending[month] = (pending[month] || 0) + bill.amount;
    }
  });

  return { collected, pending };
}

function calculatePropertyStatus(properties: any[]) {
  return {
    rented: properties.filter(p => p.status === 'Rented').length,
    vacant: properties.filter(p => p.status === 'Vacant').length
  };
}

function calculateLeaseEndings(tenants: any[]) {
  const today = new Date();
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(today.getDate() + 30);

  return tenants
    .filter(tenant => 
      tenant.end_date && 
      tenant.status === 'Active' &&
      new Date(tenant.end_date) > today &&
      new Date(tenant.end_date) <= thirtyDaysFromNow
    )
    .map(tenant => ({
      tenant_id: tenant.id,
      tenant_name: tenant.full_name,
      property_name: tenant.property?.name || 'Unknown Property',
      end_date: tenant.end_date,
      days_remaining: Math.ceil(
        (new Date(tenant.end_date).getTime() - today.getTime()) / (1000 * 3600 * 24)
      )
    }))
    .sort((a, b) => a.days_remaining - b.days_remaining);
}
